import { z } from 'zod'

/** EVM addressの厳密な文字列表現。 */
const addressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/)
/** 偶数桁hex dataの厳密な文字列表現。 */
const hexSchema = z.string().regex(/^0x(?:[0-9a-fA-F]{2})*$/)
/** 32 byte event topicの厳密な文字列表現。 */
const topicSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/)

/** 個別receipt logの計数用schema。 */
const receiptLogSchema = z.object({
  address: addressSchema,
  data: hexSchema,
  logIndex: z.number().int().nonnegative(),
  topics: z.array(topicSchema),
})

/** receipt全体の計数用schema。 */
const receiptSchema = z.object({
  status: z.union([z.literal('success'), z.literal('reverted')]),
  logs: z.array(z.unknown()),
})

/** ERC20/ERC721 Transfer event signature。 */
const transferSignature =
  '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef'
/** ERC1155 TransferSingle event signature。 */
const transferSingleSignature =
  '0xc3d58168c5ae7397731d063d5bbf3d657854427343f4c083240f7aacaa2d0f62'
/** ERC1155 TransferBatch event signature。 */
const transferBatchSignature =
  '0x4a39dc06d4c0dbc64b70af90fd698a233a518aa5d07e595d983b8c0526c8f7fb'

/** ABI動的uint256配列のoffsetと長さが整合するか検証する。 */
const isValidTransferBatchData = ({ data }: { readonly data: string }) => {
  if (data.length < 258) {
    return false
  }
  try {
    const payload = data.slice(2)
    const idsOffset = Number(BigInt(`0x${payload.slice(0, 64)}`))
    const valuesOffset = Number(BigInt(`0x${payload.slice(64, 128)}`))
    if (
      !Number.isSafeInteger(idsOffset) ||
      !Number.isSafeInteger(valuesOffset) ||
      idsOffset % 32 !== 0 ||
      valuesOffset % 32 !== 0
    ) {
      return false
    }
    const readArrayLength = (offset: number): number | undefined => {
      const start = offset * 2
      const word = payload.slice(start, start + 64)
      if (word.length !== 64) {
        return undefined
      }
      const length = Number(BigInt(`0x${word}`))
      if (
        !Number.isSafeInteger(length) ||
        start + 64 + length * 64 > payload.length
      ) {
        return undefined
      }
      return length
    }
    const idsLength = readArrayLength(idsOffset)
    const valuesLength = readArrayLength(valuesOffset)
    return (
      idsLength !== undefined &&
      idsLength === valuesLength &&
      idsOffset === 64 &&
      valuesOffset === 96 + idsLength * 32 &&
      valuesOffset * 2 + 64 + valuesLength * 64 === payload.length
    )
  } catch {
    return false
  }
}

/**
 * 取得済みEVM receiptの元ログから標準Token Transferイベント件数を数える。
 *
 * @param receipt statusと未展開ログを持つtransaction receipt。
 * @returns 正常にデコードできたERC20/ERC721/ERC1155イベントの元ログ件数。
 */
export const countTokenTransferLogs = ({
  receipt,
}: {
  readonly receipt: unknown
}): number => {
  const parsedReceipt = receiptSchema.safeParse(receipt)
  if (!parsedReceipt.success) {
    return 0
  }
  if (parsedReceipt.data.status === 'reverted') {
    return 0
  }

  let count = 0
  for (const unknownLog of parsedReceipt.data.logs) {
    const parsedLog = receiptLogSchema.safeParse(unknownLog)
    if (!parsedLog.success) {
      continue
    }
    const { data, topics } = parsedLog.data
    const signature = topics[0]?.toLowerCase()

    if (
      signature === transferSignature &&
      ((topics.length === 3 && data.length === 66) ||
        (topics.length === 4 && data === '0x')) &&
      topics.slice(1, 3).every((topic) => topic.slice(2, 26) === '0'.repeat(24))
    ) {
      count += 1
      continue
    }

    if (
      signature === transferSingleSignature &&
      topics.length === 4 &&
      data.length === 130 &&
      topics.slice(1).every((topic) => topic.slice(2, 26) === '0'.repeat(24))
    ) {
      count += 1
      continue
    }

    if (signature !== transferBatchSignature || topics.length !== 4) {
      continue
    }
    if (
      isValidTransferBatchData({ data }) &&
      topics.slice(1).every((topic) => topic.slice(2, 26) === '0'.repeat(24))
    ) {
      count += 1
    }
  }
  return count
}
